import type { RefObject } from "react"
import { useEffect } from "react"

import type {
    ActivityFieldsFragment,
    BookingInviteFieldsFragment,
    CreateNewsfeedEventInput,
} from "~graphql/generated/graphql"
import {
    BookingInternalSourceEntityType,
    EventType,
    useCreateBookingStartedEventMutation,
    useCreateListingVisitedEventMutation,
} from "~graphql/generated/graphql"
import { getInternalSourceQueryParams } from "~utils/url-param-helpers"
import useCreatePostOpenedEvent from "~utils/use-create-post-opened-event"
import useCreatePostInteractedWithEvent from "~utils/use-create-post-interacted-with-event"
import useIsVisible from "~components/shared/todo-lib-react-components/hooks/use-is-visible"
import { GENERIC_EVENT_TYPES } from "~config/constants"
import { useCreateGenericEvent } from "~utils/use-create-generic-event"

const EVENTS_FIRED_MAP: Record<string, boolean> = {}

function useCreateListingVisitedEvent() {
    const [createListingVisitedEventMutation, { loading }] =
        // we don't use the returned data in the frontend at all so to avoid unnecessary re-renders from other apollo queries we can just use no-cache
        useCreateListingVisitedEventMutation({ fetchPolicy: "no-cache" })

    const createListingVisitedEvent = (listingId: string) => {
        // dont re-send the same event if it has already been sent
        if (getOrStoreEventInLocalStorage([listingId])) {
            return
        }

        void createListingVisitedEventMutation({
            variables: { data: { listing_id: listingId } },
        })
    }

    return { createListingVisitedEvent, isLoading: loading }
}

export function useCreateBookingStartedEvent() {
    const [createBookingStartedEventMutation, { loading }] =
        // we don't use the returned data in the frontend at all so to avoid unnecessary re-renders from other apollo queries we can just use no-cache
        useCreateBookingStartedEventMutation({ fetchPolicy: "no-cache" })

    const createBookingStartedEvent = ({
        listingId,
        pricingPackageIds,
    }: {
        listingId: string
        pricingPackageIds: string[]
    }) => {
        // dont re-send the same event if it has already been sent
        if (getOrStoreEventInLocalStorage([listingId, ...pricingPackageIds])) {
            return
        }

        const sourceParams = getInternalSourceQueryParams({
            defaultEntityId: listingId,
            defaultEntityType: BookingInternalSourceEntityType.Listings,
        })

        void createBookingStartedEventMutation({
            variables: {
                data: {
                    listing_id: listingId,
                    pricing_package_ids: pricingPackageIds,
                    ...sourceParams,
                },
            },
        })
    }

    return {
        createBookingStartedEvent,
        isLoading: loading,
    }
}

export function useTrackBookingInviteStartedEvent({
    listingId,
    bookingInvite,
}: {
    listingId?: string
    bookingInvite?: BookingInviteFieldsFragment
}) {
    const { createBookingStartedEvent } = useCreateBookingStartedEvent()

    useEffect(() => {
        if (!listingId || !bookingInvite) return
        const pricingPackageIds =
            getPricingPackageIdsFromBookingInvite(bookingInvite)

        void createBookingStartedEvent({
            listingId: listingId,
            pricingPackageIds,
        })
    }, [listingId, bookingInvite])
}

function getPricingPackageIdsFromBookingInvite(
    bookingInvite: BookingInviteFieldsFragment
) {
    return bookingInvite.packages
        .map((bookingInvitePackage) => bookingInvitePackage.pricing_package?.id)
        .filter((id): id is string => !!id)
}

export function useTrackListingVisitedEvent(listingId?: string) {
    const { createListingVisitedEvent } = useCreateListingVisitedEvent()

    useEffect(() => {
        if (!listingId) return

        void createListingVisitedEvent(listingId)
    }, [listingId])
}

// For use in activity modal. The modal opens when an activity is fetched.
// This will watch for an activity and fire the event when it's truthy.
export function useTrackNewsfeedPostOpenedEvent(
    activity: ActivityFieldsFragment | null | undefined
) {
    const { createPostOpenedEvent } = useCreatePostOpenedEvent()

    useEffect(() => {
        if (!activity) return

        void fireNewsfeedEvent({
            newsfeedEventFunc: createPostOpenedEvent,
            activity,
            eventType: EventType.NewsfeedPostOpened,
        })
    }, [activity])
}

// When isEnabled is true, this hook will fire an event when the element
// in the ref becomes visible on screen. Works with nested scroll areas.
export function useTrackNewsfeedPostInteractedWithEvent({
    activity,
    isEnabled,
    divElementRef,
}: {
    activity: ActivityFieldsFragment
    isEnabled?: boolean
    divElementRef: RefObject<HTMLDivElement>
}) {
    const { createPostInteractedWithEvent } = useCreatePostInteractedWithEvent()

    const isVisible = useIsVisible(divElementRef)

    useEffect(() => {
        if (!isEnabled || !isVisible) return

        void fireNewsfeedEvent({
            newsfeedEventFunc: createPostInteractedWithEvent,
            activity,
            eventType: EventType.NewsfeedPostInteractedWith,
        })
    }, [isEnabled, isVisible])
}

async function fireNewsfeedEvent<FuncReturnType>({
    newsfeedEventFunc,
    activity,
    eventType,
}: {
    newsfeedEventFunc: (args: CreateNewsfeedEventInput) => FuncReturnType
    activity: ActivityFieldsFragment
    eventType: EventType
}) {
    const { eventArgs, shouldFireEvent } = getNewsfeedEventArgs({
        activity,
        eventType,
    })

    if (!shouldFireEvent) return

    await newsfeedEventFunc(eventArgs)
}

function getNewsfeedEventArgs({
    activity,
    eventType,
}: {
    activity: ActivityFieldsFragment
    eventType: EventType
}) {
    const eventArgs = {
        activity_id: activity.id,
        entity_type: activity.foreign_entity_type,
        entity_id: activity.foreign_id,
    }
    const eventFiredMapIds = [activity.id, eventType]
    const shouldFireEvent = !getOrStoreEventInLocalStorage(eventFiredMapIds)

    return { eventArgs, shouldFireEvent }
}

export function useTrackBookingInviteViewed(bookingInviteId?: string | null) {
    const { createEvent } = useCreateGenericEvent()

    useEffect(() => {
        // only track if we have a booking invite id
        if (!bookingInviteId) return

        if (getOrStoreEventInLocalStorage([bookingInviteId])) {
            return
        }

        void createEvent(GENERIC_EVENT_TYPES.BOOKING_INVITE_VIEWED, {
            booking_invite_id: bookingInviteId,
        })
    }, [bookingInviteId, createEvent])
}

export function useTrackPaymentMethodSelected({
    quoteId,
    paymentMethodId,
    isAchPaymentMethod,
    isExternalPaymentMethod,
}: {
    quoteId?: string | null
    paymentMethodId?: string | null
    isAchPaymentMethod?: boolean
    isExternalPaymentMethod?: boolean
}) {
    const { createEvent } = useCreateGenericEvent()

    useEffect(() => {
        if (!quoteId || !paymentMethodId) return

        if (getOrStoreEventInLocalStorage([quoteId, paymentMethodId])) {
            return
        }

        void createEvent(GENERIC_EVENT_TYPES.PAYMENT_METHOD_SELECTED, {
            quote_id: quoteId,
            payment_method_id: paymentMethodId,
            is_ach_payment_method: isAchPaymentMethod,
            is_external_payment_method: isExternalPaymentMethod,
        })
    }, [
        quoteId,
        paymentMethodId,
        isAchPaymentMethod,
        isExternalPaymentMethod,
        createEvent,
    ])
}

function getOrStoreEventInLocalStorage(eventMetadataIds: string[]) {
    const eventStorageKey = getEventStorageKey(eventMetadataIds)

    if (EVENTS_FIRED_MAP[eventStorageKey]) {
        return true
    }

    EVENTS_FIRED_MAP[eventStorageKey] = true

    return false
}

function getEventStorageKey(eventMetadataIds: string[]) {
    // join the event metadata ids create a unique key
    // this way we can stoer multiple events of the same type in local storage for different metadata's
    return eventMetadataIds.join("|")
}
